/**
 * Spec 062: the memory of a client beside its passport — the dossier on the client's page (how to
 * talk to it, what matters to it, what was promised) and the summary of an order's chat on the
 * order's «Переписка» (the talk in a few lines and how far it reaches). The order's manager writes
 * them after each answer; people read and correct them here, in versions.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { VersionConflictError } from "@/api/client";
import { ChatSummaries, ClientDossiers, type ChatSummaryOut, type ClientPassportOut, type ClientPassportRevisionOut } from "@/api/orders";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { EmptyNote } from "@/kit";
import { MEMORY_LIMIT, passportLine, revisionLine, summaryLine } from "@/lib/clientChat";
import { Markdown } from "@/lib/markdown";

interface MemoryKind<T extends ClientPassportOut> {
  title: string;
  empty: string;
  hint: string;
  key: readonly unknown[];
  load: () => Promise<T>;
  save: (body: { body_md: string; version: number }) => Promise<T>;
  history: () => Promise<ClientPassportRevisionOut[]>;
  line?: (memory: T) => string;
}

export function ClientDossierBlock({ clientId }: { clientId: string }) {
  return (
    <MemoryBlock
      kind={{
        title: "Досье",
        empty: "Досье пусто. Его ведёт менеджер клиента по ходу разговора: как общаться, что важно, чего опасается, что обещано и договорено.",
        hint: "По разделам шаблона, до 3000 знаков: устаревшее заменяй. Только наблюдения для работы — без здоровья, политики, религии и оценок человека; паролей и ключей здесь нет.",
        key: ["client-dossier", clientId],
        load: () => ClientDossiers.get(clientId),
        save: (body) => ClientDossiers.save(clientId, body),
        history: () => ClientDossiers.revisions(clientId),
      }}
    />
  );
}

export function ChatSummaryBlock({ orderId }: { orderId: string }) {
  return (
    <MemoryBlock<ChatSummaryOut>
      kind={{
        title: "Сводка разговора",
        empty: "Сводки пока нет. Менеджер пишет её после ответа клиенту: с чего начал, что выяснили, что ему ответили, на чём остановились.",
        hint: "До 3000 знаков. Суммы, даты, номера документов и имена — как есть. Сохранённая сводка доходит до последнего прочитанного сообщения.",
        key: ["chat-summary", orderId],
        load: () => ChatSummaries.get(orderId),
        save: (body) => ChatSummaries.save(orderId, body),
        history: () => ChatSummaries.revisions(orderId),
        line: (s) => summaryLine(s),
      }}
    />
  );
}

function MemoryBlock<T extends ClientPassportOut>({ kind }: { kind: MemoryKind<T> }) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const memory = useQuery({ queryKey: kind.key, queryFn: kind.load });
  if (memory.isLoading) return <SkeletonList count={1} />;
  if (!memory.data) {
    return <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void memory.refetch()}>Повторить</Button>}>{kind.title}: не загрузилось.</EmptyNote>;
  }
  const m = memory.data;
  return (
    <Card className="flex flex-col gap-2">
      <MemoryView memory={m} empty={kind.empty} line={kind.line?.(m)} />
      <div className="flex flex-wrap gap-2">
        <Button size="compact" variant="secondary" onClick={() => setEditing(true)}>{m.body_md ? "Изменить" : "Заполнить"}</Button>
        {m.version > 1 && <Button size="compact" variant="ghost" onClick={() => setHistory(true)}>История</Button>}
      </div>
      <MemorySheet key={editing ? "open" : "shut"} kind={kind} memory={m} open={editing} onClose={() => setEditing(false)} />
      <HistorySheet kind={kind} open={history} onClose={() => setHistory(false)} />
    </Card>
  );
}

/** A dossier or a summary to read; while it is empty — what it is for. */
export function MemoryView({ memory: m, empty, line }: { memory: ClientPassportOut; empty: string; line?: string }) {
  if (!m.body_md) return <p className="text-meta text-p-text-muted">{line ? `${empty} ${line}` : empty}</p>;
  return (
    <>
      <p className="text-meta text-p-text-muted">{[passportLine(m), line].filter(Boolean).join(" · ")}</p>
      <Markdown text={m.body_md} className="text-body text-p-text" />
    </>
  );
}

function MemorySheet<T extends ClientPassportOut>({ kind, memory: m, open, onClose }: { kind: MemoryKind<T>; memory: T; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  // As in the passport: the text and the version as the editor opened them; a save over someone
  // else's is a conflict, never a silent overwrite.
  const [body, setBody] = useState(m.body_md || m.template_md || "");
  const [base] = useState(m.version);
  const [newer, setNewer] = useState<T | null>(null);
  const save = useMutation({
    mutationFn: () => kind.save({ body_md: body.trim(), version: newer?.version ?? base }),
    onSuccess: () => {
      toast.success(`${kind.title}: сохранено`);
      void qc.invalidateQueries({ queryKey: kind.key });
      onClose();
    },
    onError: (error: Error) => {
      if (error instanceof VersionConflictError) {
        setNewer(error.current as unknown as T);
        toast.error("Сохранили, пока ты писал. Перенеси нужное из их текста и сохрани снова.");
      } else toast.error(error.message);
    },
  });
  return (
    <Sheet open={open} onClose={onClose} title={`${kind.title}: ${m.client}`}
      footer={<Button block disabled={!body.trim() || body.trim().length > MEMORY_LIMIT} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>Сохранить</Button>}>
      <div className="flex flex-col gap-3">
        {newer && (
          <details open className="space-y-2 rounded-xl border border-p-border px-3 py-2">
            <summary className="min-h-11 cursor-pointer py-2 text-body font-semibold">Сохранили раньше тебя — версия {newer.version}</summary>
            <Markdown text={newer.body_md} className="text-body text-p-text" />
          </details>
        )}
        <TextArea label={kind.title} value={body} onChange={(e) => setBody(e.target.value)} maxLength={MEMORY_LIMIT} className="min-h-[320px]" hint={kind.hint} />
      </div>
    </Sheet>
  );
}

function HistorySheet<T extends ClientPassportOut>({ kind, open, onClose }: { kind: MemoryKind<T>; open: boolean; onClose: () => void }) {
  const revisions = useQuery({ queryKey: [...kind.key, "revisions"], queryFn: kind.history, enabled: open });
  return (
    <Sheet open={open} onClose={onClose} title={`${kind.title}: история`}>
      {revisions.isLoading && <SkeletonList count={2} />}
      {revisions.isError && <EmptyNote>Не удалось загрузить.</EmptyNote>}
      <ol className="flex flex-col gap-2">
        {(revisions.data ?? []).map((r) => (
          <li key={r.id} className="rounded-xl border border-p-border px-3 py-2">
            <p className="text-meta text-p-text-muted">{revisionLine(r)}</p>
            <details className="space-y-2">
              <summary className="min-h-11 cursor-pointer py-2 text-body font-semibold">Текст</summary>
              <Markdown text={r.body_md} className="text-body text-p-text" />
            </details>
          </li>
        ))}
      </ol>
    </Sheet>
  );
}
